(() => {
  const featureName = 'tag-preview';
  const fieldNames = {
    dog_name: 'properties[שם הכלב]',
    phone: 'properties[מספר טלפון]'
  };
  const colorKeys = ['black', 'beige', 'red', 'blue', 'pink', 'orange', 'aqua', 'grey'];
  const mobileQuery = '(max-width: 989px)';

  const interpolate = (template, name, phone) => String(template || '')
    .split('[name]').join(name)
    .split('[phone]').join(phone);
  const dataKey = (prefix, colorKey) => `${prefix}${colorKey[0].toUpperCase()}${colorKey.slice(1)}`;

  const fitLine = (output) => {
    output.style.removeProperty('font-size');
    if (!output.textContent || output.offsetParent === null) return;
    const availableWidth = output.clientWidth;
    const requiredWidth = output.scrollWidth;
    if (!availableWidth || requiredWidth <= availableWidth) return;
    const startingSize = Number.parseFloat(window.getComputedStyle(output).fontSize);
    if (!Number.isFinite(startingSize)) return;
    const fittedSize = Math.max(6, startingSize * (availableWidth / requiredWidth) * .98);
    output.style.fontSize = `${fittedSize.toFixed(2)}px`;
  };

  const personalizedPlaceholder = (template, exampleName) => {
    const source = String(template || '').trim();
    if (!source) return exampleName;
    if (source.includes('[name]')) return source.split('[name]').join(exampleName);
    const colonIndex = Math.max(source.indexOf(':'), source.indexOf('：'));
    if (colonIndex >= 0) return `${source.slice(0, colonIndex + 1)} ${exampleName}`;
    return source.startsWith('לדוג') ? `${source}: ${exampleName}` : exampleName;
  };

  const createGalleryMedia = (preview, ownerKey) => {
    const frame = document.createElement('div');
    frame.className = 'swv6-tag-preview__gallery-frame';
    frame.dataset.swv6TagProjectionOwner = ownerKey;

    const stage = document.createElement('div');
    stage.className = 'swv6-tag-preview__gallery-stage';
    frame.append(stage);

    const heading = preview.querySelector('.swv6-tag-preview__header')?.textContent?.trim();
    if (heading) {
      const badge = document.createElement('span');
      badge.className = 'swv6-tag-preview__gallery-label';
      badge.textContent = heading;
      frame.append(badge);
    }

    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'swv6-tag-preview__gallery-thumb';
    thumb.dataset.swv6TagProjectionOwner = ownerKey;
    thumb.setAttribute('aria-label', preview.dataset.galleryThumbnailLabel || 'תצוגה אישית');
    const thumbImage = document.createElement('img');
    thumbImage.className = 'swv5-gallery__thumb-image';
    thumbImage.alt = '';
    thumbImage.loading = 'lazy';
    thumb.append(thumbImage);
    return { frame, stage, thumb, thumbImage };
  };

  const initialize = ({ root, listen }) => {
    const previews = Array.from(root.querySelectorAll('[data-swv51-tag-preview]'));
    if (!previews.length) return false;

    const mobile = window.matchMedia(mobileQuery);
    const selectedColor = () => root.swv5Purchase?.currentColor?.()
      || root.querySelector('.swv5-option--color [data-option-value]:checked')?.value
      || '';
    const normalizeColor = (value) => root.swv5Purchase?.normalizeColor?.(value)
      || window.SafeWalkV51AccessoryVariants?.normalizeColor?.(value)
      || String(value || '').trim().toLowerCase();
    const currentColorKey = () => {
      const normalizedColor = normalizeColor(selectedColor());
      return colorKeys.includes(normalizedColor) ? normalizedColor : 'black';
    };
    const states = [];

    previews.forEach((preview, index) => {
      const panel = preview.closest('.swv51-personalization');
      const controls = Array.from(panel?.querySelectorAll('input[name], input[data-safewalk-personalization-key]') || []);
      const nameInput = controls.find((input) => input.dataset.safewalkPersonalizationKey === 'dog_name'
        || input.name === fieldNames.dog_name);
      const phoneInput = controls.find((input) => input.dataset.safewalkPersonalizationKey === 'phone'
        || input.name === fieldNames.phone);
      const target = {
        image: preview.querySelector('[data-tag-preview-image]'),
        source: preview.querySelector('[data-tag-preview-source]'),
        nameOutput: preview.querySelector('[data-tag-preview-name]'),
        phoneOutput: preview.querySelector('[data-tag-preview-phone]'),
        visual: preview.querySelector('[data-tag-preview-visual]')
      };
      if (!panel || !target.nameOutput || !target.phoneOutput || !target.visual) return;

      const ownerKey = preview.dataset.previewOwner || `preview-${index + 1}`;
      const galleryEnabled = preview.dataset.previewGalleryEnabled === 'true';
      const stickyMobileEnabled = preview.dataset.stickyPreviewMobileEnabled === 'true';
      const virtualMediaId = `swv6-tag-preview-${ownerKey}`;
      const originalNamePlaceholder = nameInput?.getAttribute('placeholder') || '';
      const phonePlaceholder = preview.dataset.phonePlaceholder || '';
      const labelTemplates = {
        empty: preview.dataset.accessibilityLabelEmptyTemplate || '',
        nameOnly: preview.dataset.accessibilityLabelNameTemplate || '[name]',
        phoneOnly: preview.dataset.accessibilityLabelPhoneTemplate || '[phone]',
        both: preview.dataset.accessibilityLabelTemplate || '[name] [phone]'
      };
      const sourceAnchor = document.createComment(`swv6-tag-preview:${ownerKey}`);
      preview.before(sourceAnchor);
      let media = null;
      let dock = null;
      let blurTimer = 0;
      let editing = false;

      const setEditing = (active) => {
        if (editing === active) return;
        editing = active;
        document.body.classList.toggle('swv6-personalization-editing', active);
        root.dispatchEvent(new CustomEvent('swv5:personalization-editing', {
          bubbles: true,
          detail: { active }
        }));
      };
      const syncDockPosition = () => {
        if (!dock || dock.hidden) return;
        const viewport = window.visualViewport;
        const viewportTop = Math.max(0, viewport?.offsetTop || 0);
        const header = document.querySelector('.section-header, sticky-header, header.header');
        const headerRect = header?.getBoundingClientRect();
        const headerBottom = headerRect && headerRect.bottom > viewportTop && headerRect.top <= viewportTop + 8
          ? headerRect.bottom
          : viewportTop;
        dock.style.setProperty('--swv6-dock-top', `${Math.round(Math.max(viewportTop + 8, headerBottom + 8))}px`);
        window.requestAnimationFrame(() => {
          const input = focusedField() === 'dog_name' ? nameInput : phoneInput;
          const dockRect = dock?.getBoundingClientRect();
          const inputRect = input?.getBoundingClientRect();
          if (!dockRect || !inputRect || inputRect.top >= dockRect.bottom + 12) return;
          window.scrollBy(0, inputRect.top - dockRect.bottom - 12);
        });
      };

      const exampleName = (colorKey) => preview.dataset[dataKey('exampleName', colorKey)]
        || preview.dataset.nameFallback || preview.dataset.namePlaceholder || '';
      const previewSource = (colorKey) => preview.dataset[dataKey('previewImage', colorKey)]
        || preview.dataset.previewImageBlack || '';
      const previewSourceSet = (colorKey) => preview.dataset[dataKey('previewSrcset', colorKey)]
        || preview.dataset.previewSrcsetBlack || '';
      const previewAvifSourceSet = (colorKey) => preview.dataset[dataKey('previewAvifSrcset', colorKey)]
        || preview.dataset.previewAvifSrcsetBlack || '';
      const previewThumbnail = (colorKey) => preview.dataset[dataKey('previewThumb', colorKey)]
        || preview.dataset.previewThumbBlack || previewSource(colorKey);
      const values = () => ({
        name: (nameInput?.value || '').trim(),
        phone: (phoneInput?.value || '').trim()
      });
      const focusedField = () => document.activeElement === nameInput
        ? 'dog_name'
        : document.activeElement === phoneInput ? 'phone' : '';

      const render = () => {
        const { name, phone } = values();
        const hasName = Boolean(name);
        const hasPhone = Boolean(phone);
        const layout = hasName && hasPhone ? 'both' : hasName ? 'name-only' : hasPhone ? 'phone-only' : 'empty';
        const visibleName = layout === 'empty' ? exampleName(currentColorKey()) : name;
        const visiblePhone = layout === 'empty' ? phonePlaceholder : phone;
        const labelTemplate = layout === 'name-only'
          ? labelTemplates.nameOnly
          : layout === 'phone-only' ? labelTemplates.phoneOnly : labelTemplates[layout];
        preview.dataset.layout = layout;
        preview.dataset.hasName = String(hasName);
        preview.dataset.hasPhone = String(hasPhone);
        if (media) media.frame.dataset.layout = layout;
        target.nameOutput.textContent = visibleName;
        target.phoneOutput.textContent = visiblePhone;
        target.visual.setAttribute('aria-label', interpolate(labelTemplate, visibleName, visiblePhone));
        fitLine(target.nameOutput);
        fitLine(target.phoneOutput);
      };

      const syncColor = () => {
        const colorKey = currentColorKey();
        const source = previewSource(colorKey);
        const sourceSet = previewSourceSet(colorKey);
        const avifSourceSet = previewAvifSourceSet(colorKey);
        const thumbnail = previewThumbnail(colorKey);
        preview.dataset.previewColor = colorKey;
        if (nameInput) nameInput.placeholder = personalizedPlaceholder(originalNamePlaceholder, exampleName(colorKey));
        if (target.source) {
          if (avifSourceSet) target.source.setAttribute('srcset', avifSourceSet);
          else target.source.removeAttribute('srcset');
        }
        if (target.image) {
          if (sourceSet) target.image.setAttribute('srcset', sourceSet);
          if (source) target.image.setAttribute('src', source);
        }
        if (media?.thumbImage && thumbnail) {
          media.thumbImage.sizes = '72px';
          media.thumbImage.removeAttribute('srcset');
          media.thumbImage.src = thumbnail;
        }
        render();
      };

      const createDock = () => {
        if (!stickyMobileEnabled) return null;
        if (dock) return dock;
        dock = document.createElement('div');
        dock.className = 'swv6-tag-preview__mobile-dock';
        dock.dataset.swv6TagPreviewDock = ownerKey;
        dock.hidden = true;
        dock.setAttribute('aria-label', preview.dataset.galleryThumbnailLabel || 'תצוגה אישית');
        root.append(dock);
        return dock;
      };
      const restoreSource = () => {
        if (sourceAnchor.parentNode) sourceAnchor.parentNode.insertBefore(preview, sourceAnchor.nextSibling);
        preview.removeAttribute('data-gallery-source');
        preview.removeAttribute('data-mobile-dock');
        if (dock) dock.hidden = true;
      };
      const moveToGallery = () => {
        if (!media) return;
        media.stage.append(preview);
        preview.dataset.gallerySource = 'customer';
        preview.removeAttribute('data-mobile-dock');
        if (dock) dock.hidden = true;
        setEditing(false);
        window.requestAnimationFrame(render);
      };
      const moveToDock = () => {
        if (!stickyMobileEnabled || !mobile.matches || !media) return;
        const mobileDock = createDock();
        if (!mobileDock) return;
        mobileDock.hidden = false;
        mobileDock.append(preview);
        preview.dataset.mobileDock = 'active';
        setEditing(true);
        syncDockPosition();
        window.requestAnimationFrame(render);
      };
      const activateGalleryPreview = () => {
        if (!media || !root.swv5Gallery) return;
        root.swv5Gallery.setMedia(virtualMediaId, true, 'personalization');
        window.requestAnimationFrame(render);
      };
      const restoreEmptyPreview = () => {
        const { name, phone } = values();
        if (!focusedField() && !name && !phone && root.swv5Gallery?.isActive(virtualMediaId)) {
          root.swv5Gallery.restoreNative(false);
        }
      };
      const registerMedia = () => {
        if (media || !galleryEnabled || !root.swv5Gallery) return;
        root.swv5Gallery.unregisterVirtualMedia(virtualMediaId);
        root.querySelectorAll('[data-swv6-tag-projection-owner]').forEach((node) => {
          if (node.dataset.swv6TagProjectionOwner === ownerKey) node.remove();
        });
        const candidate = createGalleryMedia(preview, ownerKey);
        const registered = root.swv5Gallery.registerVirtualMedia({
          id: virtualMediaId,
          frame: candidate.frame,
          thumb: candidate.thumb,
          preserveOnVariantChange: true
        });
        if (!registered) return;
        media = candidate;
        moveToGallery();
        syncColor();
      };
      const unregisterMedia = () => {
        window.clearTimeout(blurTimer);
        restoreSource();
        setEditing(false);
        if (media) root.swv5Gallery?.unregisterVirtualMedia(virtualMediaId);
        media = null;
        dock?.remove();
        dock = null;
      };
      const syncMode = () => {
        registerMedia();
        if (!media) return;
        const field = focusedField();
        if (field) {
          preview.dataset.activeField = field;
          media.frame.dataset.activeField = field;
          activateGalleryPreview();
          if (mobile.matches) moveToDock();
          else moveToGallery();
        } else {
          moveToGallery();
        }
      };
      const setActiveField = (fieldKind) => {
        window.clearTimeout(blurTimer);
        preview.dataset.activeField = fieldKind;
        if (media) media.frame.dataset.activeField = fieldKind;
        registerMedia();
        activateGalleryPreview();
        if (mobile.matches) moveToDock();
      };
      const clearActiveField = () => {
        window.clearTimeout(blurTimer);
        blurTimer = window.setTimeout(() => {
          const field = focusedField();
          if (field) {
            setActiveField(field);
            return;
          }
          preview.removeAttribute('data-active-field');
          media?.frame.removeAttribute('data-active-field');
          moveToGallery();
          restoreEmptyPreview();
        }, 0);
      };

      [nameInput, phoneInput].forEach((input) => {
        if (!input) return;
        listen(input, 'input', () => {
          render();
          activateGalleryPreview();
          if (mobile.matches && focusedField()) moveToDock();
        });
        listen(input, 'change', () => {
          render();
          activateGalleryPreview();
        });
      });
      if (nameInput) {
        listen(nameInput, 'focus', () => setActiveField('dog_name'));
        listen(nameInput, 'blur', clearActiveField);
      }
      if (phoneInput) {
        listen(phoneInput, 'focus', () => setActiveField('phone'));
        listen(phoneInput, 'blur', clearActiveField);
      }
      listen(root, 'swv5:gallery-ready', syncMode);
      listen(document, 'shopify:block:select', (event) => {
        const selectedBlock = event.target;
        if (selectedBlock !== preview && !selectedBlock?.contains?.(preview) && !preview.contains(selectedBlock)) return;
        registerMedia();
        activateGalleryPreview();
      });
      listen(mobile, 'change', syncMode);
      listen(window, 'resize', render);
      if (window.visualViewport) {
        listen(window.visualViewport, 'resize', syncDockPosition);
        listen(window.visualViewport, 'scroll', syncDockPosition);
      }

      states.push({
        syncColor,
        syncMode,
        cleanup: () => {
          unregisterMedia();
          sourceAnchor.remove();
          if (nameInput) nameInput.setAttribute('placeholder', originalNamePlaceholder);
        }
      });
      syncColor();
      syncMode();
    });

    listen(root, 'swv5:variant-change', () => states.forEach((state) => state.syncColor()));
    return () => states.reverse().forEach((state) => state.cleanup());
  };

  if (window.SafeWalkV51Product?.register) {
    window.SafeWalkV51Product.register(featureName, initialize);
    return;
  }
  window.SafeWalkV51ProductQueue = window.SafeWalkV51ProductQueue || [];
  window.SafeWalkV51ProductQueue.push([featureName, initialize]);
})();
